/**
 * Variant Modal Manager for Fyncall Widget
 * Handles product variant selection, quantity controls, and add-to-cart functionality
 */

import { getColorCode, isColorNameLight } from '../utils/color-helpers.js';
import { formatPrice, calculateDiscountPercent } from '../utils/formatting.js';

/**
 * Variant modal state
 */
let currentProduct = null;
let selectedVariant = null;
let selectedQuantity = 1;

/**
 * Callbacks for UI updates (set by init)
 */
let callbacks = {
    onRender: null,      // Called when modal needs re-rendering
    onClose: null,       // Called when modal closes
    onSendMessage: null, // Called to send chat message
    onShowStatus: null,  // Called to show status message
    onUpdateCart: null   // Called when cart is updated
};

/**
 * Initialize variant modal with callbacks
 * @param {Object} cbs - Callback functions
 */
export function initVariantModal(cbs) {
    callbacks = { ...callbacks, ...cbs };
}

/**
 * Get current modal state
 * @returns {Object} Current state
 */
export function getVariantModalState() {
    return {
        product: currentProduct,
        variant: selectedVariant,
        quantity: selectedQuantity
    };
}

/**
 * Open variant modal for a product
 * @param {Object} product - Product object
 */
export function openVariantModal(product) {
    currentProduct = product;
    selectedVariant = product.variants?.[0] || null;
    selectedQuantity = 1;
    return { product: currentProduct, variant: selectedVariant, quantity: selectedQuantity };
}

/**
 * Close variant modal and reset state
 */
export function closeVariantModal() {
    currentProduct = null;
    selectedVariant = null;
    selectedQuantity = 1;
}

/**
 * Group variants by option name (Size, Color, Style)
 * @param {Array} variants - Product variants
 * @returns {Map} Option groups
 */
export function groupVariantOptions(variants) {
    const groups = new Map();
    variants.forEach(variant => {
        if (variant.option1) {
            if (!groups.has('Size')) groups.set('Size', new Set());
            groups.get('Size').add(variant.option1);
        }
        if (variant.option2) {
            if (!groups.has('Color')) groups.set('Color', new Set());
            groups.get('Color').add(variant.option2);
        }
        if (variant.option3) {
            if (!groups.has('Style')) groups.set('Style', new Set());
            groups.get('Style').add(variant.option3);
        }
    });
    return groups;
}

/**
 * Find variant matching an option
 * @param {Array} variants - Product variants
 * @param {string} optionName - Option name (Size, Color, Style)
 * @param {string} value - Option value
 * @returns {Object|undefined} Matching variant
 */
export function findVariantByOption(variants, optionName, value) {
    return variants.find(v => {
        if (optionName === 'Size' && v.option1 === value) return true;
        if (optionName === 'Color' && v.option2 === value) return true;
        if (optionName === 'Style' && v.option3 === value) return true;
        return false;
    });
}

/**
 * Check if an option is currently selected
 * @param {string} optionName - Option name
 * @param {string} value - Option value
 * @returns {boolean} Whether option is selected
 */
export function isOptionSelected(optionName, value) {
    if (!selectedVariant) return false;
    if (optionName === 'Size') return selectedVariant.option1 === value;
    if (optionName === 'Color') return selectedVariant.option2 === value;
    if (optionName === 'Style') return selectedVariant.option3 === value;
    return false;
}

/**
 * Select a variant option
 * @param {string} optionName - Option name
 * @param {string} value - Option value
 * @returns {Object|null} New selected variant
 */
export function selectVariantOption(optionName, value) {
    if (!currentProduct || !currentProduct.variants) return null;

    const variants = currentProduct.variants;
    const currentVariant = selectedVariant || variants[0];

    let newVariant = variants.find(v => {
        if (optionName === 'Size') {
            return v.option1 === value && v.option2 === currentVariant?.option2;
        }
        if (optionName === 'Color') {
            return v.option1 === currentVariant?.option1 && v.option2 === value;
        }
        if (optionName === 'Style') {
            return v.option1 === currentVariant?.option1 && v.option2 === currentVariant?.option2 && v.option3 === value;
        }
        return false;
    });

    // If no exact match, find any variant with this option
    if (!newVariant) {
        newVariant = findVariantByOption(variants, optionName, value);
    }

    if (newVariant) {
        selectedVariant = newVariant;
    }

    return selectedVariant;
}

/**
 * Update quantity
 * @param {string} action - 'increase' or 'decrease'
 * @param {number} maxQty - Maximum quantity allowed
 * @returns {number} New quantity
 */
export function updateQuantity(action, maxQty = 10) {
    if (action === 'increase' && selectedQuantity < maxQty) {
        selectedQuantity++;
    } else if (action === 'decrease' && selectedQuantity > 1) {
        selectedQuantity--;
    }
    return selectedQuantity;
}

/**
 * Get stock status for display
 * @param {number|undefined} inventoryQty - Inventory quantity
 * @returns {Object} Stock status details
 */
export function getStockStatus(inventoryQty) {
    let status = 'in-stock';
    let message = '✓ In Stock';
    let dotClass = 'green';

    if (inventoryQty <= 0) {
        status = 'out-of-stock';
        message = '✕ Out of Stock';
        dotClass = 'red';
    } else if (inventoryQty <= 5) {
        status = 'low-stock';
        message = `⚠ Only ${inventoryQty} left!`;
        dotClass = 'orange';
    }

    return { status, message, dotClass };
}

/**
 * Generate variant modal body HTML
 * @param {Object} product - Current product
 * @param {Object} variant - Selected variant
 * @param {number} quantity - Selected quantity
 * @returns {string} HTML string
 */
export function generateVariantModalHTML(product, variant, quantity) {
    if (!product) return '';

    const variants = product.variants || [];
    const selectedVar = variant || variants[0];
    const price = selectedVar?.price || product.price;
    const compareAtPrice = selectedVar?.compare_at_price || product.compare_at_price;
    const inventoryQty = selectedVar?.inventory_quantity ?? product.inventory_quantity;

    // Get variant-specific image if available
    const displayImage = selectedVar?.image_url || product.image_url;

    // Calculate discount percentage
    let discountPercent = 0;
    if (compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price)) {
        discountPercent = Math.round((1 - parseFloat(price) / parseFloat(compareAtPrice)) * 100);
    }

    // Get stock status
    const { status: stockStatus, message: stockMessage, dotClass: stockDotClass } = getStockStatus(inventoryQty);

    // Group variants by option name
    const optionGroups = groupVariantOptions(variants);

    let html = `
        <div class="fyncall-variant-preview">
            <div class="fyncall-variant-preview-image">
                ${displayImage ? `<img src="${displayImage}" alt="${product.name}">` : '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:32px;">📦</div>'}
            </div>
            <div class="fyncall-variant-preview-info">
                <div class="fyncall-variant-preview-name">${product.name || 'Product'}</div>
                <div class="fyncall-variant-price-row">
                    <span class="fyncall-variant-preview-price">$${parseFloat(price).toFixed(2)}</span>
                    ${compareAtPrice && discountPercent > 0 ? `
                        <span class="fyncall-variant-original-price">$${parseFloat(compareAtPrice).toFixed(2)}</span>
                        <span class="fyncall-variant-sale-badge">${discountPercent}% OFF</span>
                    ` : ''}
                </div>
                <div class="fyncall-variant-stock ${stockStatus}">
                    <span class="stock-dot ${stockDotClass}"></span>
                    <span>${stockMessage}</span>
                </div>
                ${selectedVar ? `
                    <div class="fyncall-variant-selected-summary">
                        Selected: ${[selectedVar.option1, selectedVar.option2, selectedVar.option3].filter(Boolean).join(' / ')}
                    </div>
                ` : ''}
            </div>
        </div>
    `;

    // Render option groups
    optionGroups.forEach((values, optionName) => {
        const isColor = optionName.toLowerCase().includes('color') || optionName.toLowerCase().includes('colour');
        const isSize = optionName.toLowerCase().includes('size');

        html += `
            <div class="fyncall-variant-group">
                <div class="fyncall-variant-label-row">
                    <label class="fyncall-variant-label">${optionName}</label>
                    ${isSize ? `<span class="fyncall-variant-size-guide" data-action="size-guide">📏 Size Guide</span>` : ''}
                </div>
                <div class="fyncall-variant-options">
                    ${Array.from(values).map(value => {
                        const matchingVariant = findVariantByOption(variants, optionName, value);
                        const isSelected = isOptionSelected(optionName, value);
                        const isAvailable = matchingVariant && (matchingVariant.inventory_quantity > 0 || matchingVariant.inventory_quantity === undefined);
                        const colorCode = isColor ? getColorCode(value) : '';
                        const isLightColor = isColor ? isColorNameLight(colorCode) : false;
                        const colorStyle = isColor ? `background-color: ${colorCode};` : '';

                        return `
                            <button
                                class="fyncall-variant-option ${isColor ? 'color' : ''} ${isColor && isLightColor ? 'light' : ''} ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}"
                                data-option="${optionName}"
                                data-value="${value}"
                                ${!isAvailable ? 'disabled title="Out of stock"' : ''}
                                style="${colorStyle}"
                            >${isColor ? '' : value}</button>
                        `;
                    }).join('')}
                </div>
            </div>
        `;
    });

    // Notify when available section (if selected variant is out of stock)
    if (inventoryQty <= 0) {
        html += `
            <div class="fyncall-notify-available">
                <div class="fyncall-notify-available-text">🔔 Get notified when this item is back in stock</div>
                <div class="fyncall-notify-available-form">
                    <input type="email" class="fyncall-notify-email" placeholder="Enter your email" id="fyncall-notify-email-input">
                    <button class="fyncall-notify-btn" data-action="notify-me">Notify Me</button>
                </div>
            </div>
        `;
    }

    // Quantity selector (only show if in stock)
    if (inventoryQty > 0) {
        const maxQty = Math.min(inventoryQty, 10);
        html += `
            <div class="fyncall-quantity-selector">
                <span class="fyncall-quantity-label">Quantity</span>
                <div class="fyncall-quantity-controls">
                    <button class="fyncall-quantity-btn" data-action="decrease" ${quantity <= 1 ? 'disabled' : ''}>−</button>
                    <span class="fyncall-quantity-value">${quantity}</span>
                    <button class="fyncall-quantity-btn" data-action="increase" ${quantity >= maxQty ? 'disabled' : ''}>+</button>
                </div>
                ${inventoryQty <= 10 ? `<span style="font-size:11px;color:#6b7280;margin-left:auto;">(Max: ${inventoryQty})</span>` : ''}
            </div>
        `;
    }

    return html;
}

/**
 * Handle notify me form submission
 * @param {HTMLInputElement} emailInput - Email input element
 * @param {Function} sendMessage - Function to send chat message
 * @returns {Object} Result with success status and email
 */
export function handleNotifyMe(emailInput, sendMessage) {
    const email = emailInput?.value?.trim();

    if (!email || !email.includes('@')) {
        return { success: false, error: 'Invalid email' };
    }

    const product = currentProduct;
    const variant = selectedVariant;

    // Build notification message
    const message = `Please notify me when "${product?.name}" ${variant?.option1 ? `(${[variant.option1, variant.option2, variant.option3].filter(Boolean).join(', ')})` : ''} is back in stock. My email: ${email}`;

    if (sendMessage) {
        sendMessage(message);
    }

    return { success: true, email };
}

/**
 * Request size guide via chat
 * @param {Function} sendMessage - Function to send chat message
 */
export function requestSizeGuide(sendMessage) {
    const product = currentProduct;
    const message = `Can you show me the size guide for "${product?.name}"?`;

    if (sendMessage) {
        sendMessage(message);
    }
}

/**
 * Add selected variant to cart
 * @returns {Object} Cart add details
 */
export function getAddToCartDetails() {
    const product = currentProduct;
    const variant = selectedVariant;

    if (!product) return null;

    const variantId = variant?.shopify_variant_id || variant?.id || product.shopify_variant_id || product.variant_id;

    return {
        product,
        variant,
        variantId,
        quantity: selectedQuantity,
        publicUrl: product.public_url
    };
}

/**
 * Add to cart via Shopify AJAX API
 * @param {string|number} variantId - Variant ID
 * @param {number} quantity - Quantity to add
 * @returns {Promise<Object>} Response result
 */
export async function addToCart(variantId, quantity) {
    const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
    });

    if (!response.ok) {
        throw new Error('Failed to add to cart');
    }

    return response.json();
}

// Re-export color helpers for convenience
export { getColorCode, isColorNameLight };
